import React, { useEffect } from 'react';
import {
  Box,
  Button,
  List,
  ListItem,
  ListItemText,
  Switch,
  Typography,
} from '@mui/material';

import { usePlatformNavigation } from 'src/utils/navigation';
import SearchInput from './SearchInput';
import OrganizationsCheckboxNode from './OrganizationsCheckboxNode';
import ActionButtons from './ActionButtons';
import { useTranslation } from 'react-i18next';
import {
  BranchTagWithCountry,
  useBranchesWithCountry,
} from 'src/utils/branches';
import { useAppDispatch, useAppSelector } from 'src/redux-file/hooks';
import { setSelectedBranches } from 'src/redux-file/ghgSuperdashboard/slice';
import { useSupplierSurvey } from 'src/Ghg/Scope3GoodsAndServices/hooks';
import { OrganizationTag } from 'src/utils/tags';
import { WarningAmberOutlined, WarningOutlined } from '@mui/icons-material';
import CountryGroupHeader from 'src/Superadmin/components/CountryGroupHeader';
import {
  countryKeysOf,
  useCountryGroupCollapse,
} from 'src/Superadmin/components/CountryGroupHeader/useCountryGroupCollapse';

// specs/001-raben-country-masteradmin/mass-specs/specs/
// 20260826-fe-country-group-folding.md (Goal 2/BR-2/BR-2a/BR-2b, corrected
// after review). This sidebar is red -- CountryGroupBulkControls and
// CountryRollup (frontend/src/Superadmin/components/CountryGroupHeader) are
// styled for the light Structure/Users screens, so they are deliberately
// NOT used here (that shared file is out of scope for this fix). Two
// consequences, both local to this file:
//
// BR-2a: no roll-up. The country header already has its own Switch right
// next to the name, so a `<on>/<total>` number beside it says the same
// thing twice, and it rendered as dark text on the red ground besides. A
// partial selection is instead carried by the SWITCH's own appearance (a
// visually distinct half-way state -- see PARTIAL_SWITCH_SX below), never a
// number. Structure has no country-level control at all, and Users' `Cały
// kraj` is a grant rather than a running count, so both of those keep the
// roll-up (BR-2) -- this exclusion is CompanyFilter-only.
//
// BR-2b: the bulk fold buttons are restyled as small outline pills (white
// text/border on transparent, matching the mockup's `.mini` treatment)
// instead of the light-background Button variants CountryGroupBulkControls
// uses, which rendered invisibly on red.
const REDGROUND_PILL_SX = {
  textTransform: 'none' as const,
  background: 'transparent',
  border: '1px solid rgba(255,255,255,0.5)',
  color: '#fff',
  borderRadius: '999px',
  fontSize: '11px',
  lineHeight: 1.6,
  padding: '2px 10px',
  minWidth: 0,
  fontWeight: 600,
  '&:hover': {
    background: 'rgba(255,255,255,0.14)',
    border: '1px solid rgba(255,255,255,0.5)',
  },
};

// BR-2a's "visually distinct half-way appearance": the mockup's `.sw2.part`
// sits the thumb roughly mid-track with a lighter track fill, instead of
// hard left (off) or hard right (on). MUI's Switch has no built-in
// indeterminate state, so this overrides its public, documented class hooks
// (.MuiSwitch-switchBase/.MuiSwitch-track -- the same hooks MUI's own
// customization docs use) rather than the `checked` boolean, which stays
// exactly the "every branch selected" semantics it always had (BR-1)
// -- purely a cosmetic layer on top.
const PARTIAL_SWITCH_SX = {
  '& .MuiSwitch-switchBase': {
    transform: 'translateX(8px) !important',
  },
  '& .MuiSwitch-track': {
    opacity: '0.75 !important',
  },
};

// C10 Goal 1 -- sidebar branch switches grouped under plain country header
// rows. BR-1: a header's own Switch is pure FE convenience over the SAME
// `selectedBranches` redux state the individual branch switches already
// write to (no new control type, no new source of truth) -- toggling it
// just fans out to every branch switch in the group in one dispatch.
interface CountryGroup {
  code: string | null;
  name: string | null;
  branches: BranchTagWithCountry[];
}

// specs/001-raben-country-masteradmin/mass-specs/specs/
// 20260826-fe-country-group-folding.md ("vertical variant"). Reused as both
// the React key (unchanged from before this feature) and the fold-state key
// handed to useCountryGroupCollapse -- one sentinel, one meaning.
const NO_COUNTRY_KEY = '__no_country__';

function groupKey(group: CountryGroup): string {
  return group.code ?? NO_COUNTRY_KEY;
}

function groupByCountry(branches: BranchTagWithCountry[]): CountryGroup[] {
  const byCode = new Map<string, CountryGroup>();
  branches.forEach((branch) => {
    const key = branch.country_code ?? '';
    if (!byCode.has(key)) {
      byCode.set(key, {
        code: branch.country_code,
        name: branch.country_name,
        branches: [],
      });
    }
    byCode.get(key)!.branches.push(branch);
  });
  const groups = Array.from(byCode.values());
  const withCountry = groups
    .filter((group) => group.code !== null)
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  // EC-3: the null-country bucket ("Bez kraju") always sorts last.
  const withoutCountry = groups.filter((group) => group.code === null);
  return [...withCountry, ...withoutCountry];
}

export default function CompanyFilter() {
  const { t } = useTranslation();
  const { productOrganizations } = usePlatformNavigation();
  const { branches } = useBranchesWithCountry();
  const { selectedBranches, selectedCompanies } = useAppSelector(
    (s) => s.ghgSuperdashboard
  );
  const user = useAppSelector((s) => s.platform.user);
  const dispatch = useAppDispatch();
  const orgIds = productOrganizations.map((org) => org.id);
  const topOrganizations = productOrganizations.filter(
    (org) => !orgIds.includes(org.parent_id)
  );

  const rabenRoot = productOrganizations?.find((org) =>
    org.tags?.includes(OrganizationTag.RabenRoot)
  );

  const { data } = useSupplierSurvey(
    rabenRoot?.id,
    undefined,
    undefined,
    branches.map((branch) => branch.name)
  );

  useEffect(() => {
    if (branches?.length) {
      dispatch(
        setSelectedBranches({
          organizations: productOrganizations,
          selectedBranches: branches.map((b) => b.name),
        })
      );
    }
  }, [branches]);
  const visibleCount =
    data?.filter(
      (survey) =>
        selectedBranches.length > 0 ||
        (survey.organization_id &&
          selectedCompanies.includes(survey.organization_id))
    ).length || 0;

  const notVisibleCount = (data?.length || 0) - visibleCount;

  const updateSelectedBranches = (branch: string, value: boolean) => {
    if (value && !selectedBranches.includes(branch)) {
      dispatch(
        setSelectedBranches({
          organizations: productOrganizations,
          selectedBranches: [...selectedBranches, branch],
        })
      );
    } else if (!value && selectedBranches.includes(branch)) {
      dispatch(
        setSelectedBranches({
          organizations: productOrganizations,
          selectedBranches: selectedBranches.filter((b) => b !== branch),
        })
      );
    }
  };

  // BR-1: proxies the exact same action every individual branch switch
  // above already dispatches -- just fanned out over the group in one go.
  const toggleCountryGroup = (group: CountryGroup, value: boolean) => {
    const groupNames = group.branches.map((branch) => branch.name);
    const nextSelected = value
      ? Array.from(new Set([...selectedBranches, ...groupNames]))
      : selectedBranches.filter((name) => !groupNames.includes(name));
    dispatch(
      setSelectedBranches({
        organizations: productOrganizations,
        selectedBranches: nextSelected,
      })
    );
  };

  const groupedBranches = React.useMemo(
    () => groupByCountry(branches),
    [branches]
  );

  // specs/001-raben-country-masteradmin/mass-specs/specs/
  // 20260826-fe-country-group-folding.md (Goal 1/3/5, vertical variant,
  // corrected). Every group starts collapsed on first visit regardless of
  // scope -- see StructureTable.tsx's identical comment for why "expand my
  // countries" was wrong. `allCountryKeys` is used only by
  // collapseAll()/expandAll() below.
  const allCountryKeys = React.useMemo(
    () => countryKeysOf(branches, NO_COUNTRY_KEY),
    [branches]
  );
  const storageKey = `dashboard.countryFold.companyFilter.${
    user?.id ?? 'anon'
  }`;
  const collapse = useCountryGroupCollapse({
    storageKey,
    allKeys: allCountryKeys,
  });

  // EC-6: see StructureTable.tsx's identical comment -- a caller whose own
  // branches span a single country renders that lone group expanded, not
  // collapsible into uselessness.
  const resolveOpen = (key: string) =>
    (groupedBranches.length === 1 && !collapse.isFullyCollapsed) ||
    collapse.isOpen(key);

  const anyGroupOpen = groupedBranches.some((group) =>
    collapse.isOpen(groupKey(group))
  );

  return (
    <>
      <Box sx={{ m: '8px 4px', mt: '0px' }}>
        <SearchInput />
        <ActionButtons />
      </Box>
      {/* BR-2b: heading and bulk controls share one row (mirrors the
          mockup's `.sechead`) -- keeping them stacked as two separate
          blocks is what left an oversized, unexplained-looking gap here
          even before the buttons were invisible. */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: '8px',
        }}
      >
        <ListItemText sx={{ p: '4px 0', flex: '0 1 auto' }}>
          {t('superdashboardGhg.sidebar.organizations.branches')}
        </ListItemText>
        {/* Mockup A″ (`.sechead`): ONE pill next to the heading, its label
            flipping with the fold state -- two side-by-side pills wrapped
            into a cramped stack at this sidebar width. Keyed off the
            groups' EFFECTIVE openness, not `isFullyCollapsed`, which is
            true only after an EXPLICIT collapse-all (EC-6 semantics) and
            so reads wrong on a first visit where everything merely
            DEFAULTS to closed. */}
        <Button
          size="small"
          sx={REDGROUND_PILL_SX}
          onClick={anyGroupOpen ? collapse.collapseAll : collapse.expandAll}
        >
          {t(
            anyGroupOpen
              ? 'superdashboardGhg.sidebar.countryFolding.bulk.collapseAll'
              : 'superdashboardGhg.sidebar.countryFolding.bulk.expandAll'
          )}
        </Button>
      </Box>
      <Box>
        <List>
          {groupedBranches.map((group) => {
            const key = groupKey(group);
            const isOpen = resolveOpen(key);
            const label = group.code
              ? (group.name as string)
              : String(t('superdashboardGhg.sidebar.organizations.noCountry'));
            const onCount = group.branches.filter((branch) =>
              selectedBranches.includes(branch.name)
            ).length;
            // Unchanged from before this fix (BR-1): ON means every branch
            // in the group is selected, exactly as the Switch below already
            // checks.
            const allSelected = group.branches.every((branch) =>
              selectedBranches.includes(branch.name)
            );
            // BR-2a: neither "none" nor "all" -- carried by the switch's own
            // half-way appearance below, never a number.
            const isPartial = onCount > 0 && !allSelected;
            return (
              <React.Fragment key={key}>
                <ListItem>
                  <ListItemText sx={{ p: '4px 8px' }}>
                    <Box
                      display="flex"
                      flexDirection="row"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                        }}
                      >
                        <CountryGroupHeader
                          label={label}
                          isOpen={isOpen}
                          onToggle={() => collapse.toggle(key)}
                          actionLabel={String(
                            t(
                              isOpen
                                ? 'superdashboardGhg.sidebar.countryFolding.collapseGroup'
                                : 'superdashboardGhg.sidebar.countryFolding.expandGroup',
                              { name: label, count: group.branches.length }
                            )
                          )}
                        />
                      </Box>
                      <Switch
                        color="secondary"
                        aria-label={`Toggle ${label} group`}
                        checked={allSelected}
                        onChange={(_, checked) =>
                          toggleCountryGroup(group, checked)
                        }
                        data-partial={isPartial || undefined}
                        sx={isPartial ? PARTIAL_SWITCH_SX : undefined}
                      />
                    </Box>
                  </ListItemText>
                </ListItem>
                {isOpen &&
                  group.branches.map((branch) => (
                    <ListItem key={branch.name}>
                      <Switch
                        color="secondary"
                        aria-label={`Toggle ${branch.description}`}
                        onChange={(_, checked) =>
                          updateSelectedBranches(branch.name, checked)
                        }
                        checked={selectedBranches.includes(branch.name)}
                      />
                      <Typography>{branch.description}</Typography>
                    </ListItem>
                  ))}
              </React.Fragment>
            );
          })}
        </List>
      </Box>
      {notVisibleCount > 0 && (
        <ListItem>
          <ListItemText sx={{ p: '4px 8px' }}>
            <Box display="flex" flexDirection="row" alignItems="center">
              <WarningAmberOutlined sx={{ mr: '4px' }} />
              {notVisibleCount}{' '}
              {t('superdashboardGhg.sidebar.organizations.notVisible')}
            </Box>
          </ListItemText>
        </ListItem>
      )}
      <ListItem>
        <ListItemText sx={{ p: '4px 8px' }}>
          {t('superdashboardGhg.sidebar.organizations.title')}
        </ListItemText>
      </ListItem>
      {/* Fills whatever the drawer column has left between the filters above
          and the sidebar footer below. The old fixed calc(100% - 360px) did
          not match the real height of the filters, so the list ran past the
          bottom of the drawer, under the help / API links. */}
      <Box
        sx={{
          flex: '1 1 0',
          minHeight: '160px',
          overflowY: 'scroll',
          marginTop: '8px',
        }}
      >
        {topOrganizations.map((org) => (
          <OrganizationsCheckboxNode key={org.id} organization={org} />
        ))}
      </Box>
    </>
  );
}
